import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { useAppContextNonNull } from "../../contexts/AppContext";
import { IProductionDashboardStepDTO, IProductionDashboardResponseDTO } from "../../models/DTO/Production/IProductionResponseDTO";
import EndpointsEnum from "../../models/EndpointsEnum";
import * as ProductionService from "../../services/ProductionService";
import { joinSupplierRoom } from "../../services/RoomService";
import ProductionThroughputChart from "./ProductionThroughputChart";
import ProductionStepDrilldown from "./ProductionStepDrilldown";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";
import "./ProductionDashboard.scss";

function getBottleneckLevel(step: IProductionDashboardStepDTO, avgCount: number): "green" | "yellow" | "red" {
    if (avgCount === 0) return "green";
    if (step.serialCount > avgCount * 2) return "red";
    if (step.serialCount > avgCount * 1.5) return "yellow";
    return "green";
}

const ProductionDashboard: React.FC = () => {
    const { userModel, supplierModel, vendorModel, socket } = useAppContextNonNull();
    const ctx = { userModel, supplierModel, vendorModel };

    const [dashboardData, setDashboardData] = useState<IProductionDashboardResponseDTO | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedStep, setSelectedStep] = useState<IProductionDashboardStepDTO | null>(null);

    const fetchDashboard = useCallback(async () => {
        try {
            const data = await ProductionService.getProductionDashboard(ctx);
            data.steps.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
            setDashboardData(data);
        } catch (err) {
            console.error("Failed to fetch production dashboard", err);
        }
    }, [userModel.guid, supplierModel.guid]);

    // Join supplier room for real-time events
    useEffect(() => {
        joinSupplierRoom(socket, userModel, supplierModel);
    }, [socket, userModel.guid, supplierModel.guid]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await fetchDashboard();
            setIsLoading(false);
        };
        init();
    }, [fetchDashboard]);

    // Real-time: refresh when serials are advanced
    useEffect(() => {
        if (!socket) return;

        const handler = () => {
            fetchDashboard();
        };

        socket.on(EndpointsEnum.ProductionSerialsAdvanced, handler);
        return () => {
            socket.off(EndpointsEnum.ProductionSerialsAdvanced, handler);
        };
    }, [socket, fetchDashboard]);

    const productionSteps = useMemo(() => {
        return dashboardData?.steps || [];
    }, [dashboardData]);

    const avgCount = productionSteps.length > 0 ? productionSteps.reduce((sum, s) => sum + s.serialCount, 0) / productionSteps.length : 0;

    if (isLoading) {
        return (
            <div className="pd-dashboard d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" />
                <span className="ms-3">Loading Production Dashboard...</span>
            </div>
        );
    }

    if (selectedStep) {
        return (
            <div className="pd-dashboard">
                <ProductionStepDrilldown step={selectedStep} ctx={ctx} onBack={() => setSelectedStep(null)} />
            </div>
        );
    }

    return (
        <div className="pd-dashboard">
            <div className="pd-dashboard__header">
                <h3 className="pd-dashboard__title">Production Dashboard</h3>
                <small className="text-muted">Live updates</small>
            </div>

            {productionSteps.length === 0 ? (
                <div className="text-muted text-center py-5">No production workflow steps configured or no serials in production.</div>
            ) : (
                <>
                    <div className="pd-dashboard__cards">
                        {productionSteps.map((step) => {
                            const level = getBottleneckLevel(step, avgCount);
                            return (
                                <div
                                    key={step.stepId ?? step.stepName}
                                    className={`pd-dashboard__card pd-dashboard__card--${level}`}
                                    onClick={() => setSelectedStep(step)}>
                                    <div className="pd-dashboard__card-name">
                                        <OwTranslate id={step.stepName} defaultMessage={step.stepName} />
                                    </div>
                                    <div className="pd-dashboard__card-count">{step.serialCount}</div>
                                    <div className="pd-dashboard__card-label">serials</div>
                                    <div className="pd-dashboard__card-throughput">
                                        <i className="fas fa-arrow-right me-1" />
                                        {step.dailyThroughput} / day
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <ProductionThroughputChart steps={productionSteps} />
                </>
            )}
        </div>
    );
};

export default ProductionDashboard;
